import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface CartItem {
  id: string;
  itemId: number;
  name: string;
  price: number;
  quantity: number;
  imageUrl: string;
  options?: any[];
  extras?: {
    group: string;
    choice: string;
    price: number;
  }[];
}

export type OrderType = 'delivery' | 'collection';

export interface CartSummaryConfig {
  items: CartItem[];
  orderType: OrderType;
  deliveryFee: number;
  serviceFee: number;
  minimumOrderAmount: number;
  minimumDeliveryAmount: number;
  smallOrderFee: number;
}

export interface CartSummary {
  subtotal: number;
  deliveryFee: number;
  serviceFee: number;
  smallOrderFee: number;
  total: number;
  minimumGuardTotal: number;
  isBelowMinimum: boolean;
  amountRemainingToMinimum: number;
}

export interface PaymentSummarySnapshot {
  items: CartItem[];
  orderNotes: string;
  orderType: OrderType;
  deliveryFee: number;
  serviceFee: number;
  smallOrderFee: number;
  total: number;
}

const CART_STORAGE_KEY = 'waitron-cart';
const ORDER_NOTES_KEY = 'waitron.orderNotes';
const PAYMENT_SUMMARY_KEY = 'waitron.paymentSummary';

@Injectable({
  providedIn: 'root',
})
export class CartService {
  private cart: CartItem[] = this.loadCartFromStorage();
  private cartSubject = new BehaviorSubject<CartItem[]>([...this.cart]);
  cart$ = this.cartSubject.asObservable();

  constructor() {
    this.cart$.subscribe((updatedCart) => {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(updatedCart));
    });
  }

  private toNumber(value: unknown): number {
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
  }

  private normalizeCartItem(item: any): CartItem {
    return {
      id: String(item?.id ?? ''),
      itemId: this.toNumber(item?.itemId ?? item?.itemID ?? item?.menuItemId),
      name: String(item?.name ?? ''),
      price: this.toNumber(item?.price),
      quantity: this.toNumber(item?.quantity) || 1,
      imageUrl: item?.imageUrl ?? item?.imageurl ?? '',
      options: Array.isArray(item?.options) ? item.options : [],
      extras: Array.isArray(item?.extras)
        ? item.extras.map((extra: any) => ({
            group: String(extra?.group ?? ''),
            choice: String(extra?.choice ?? ''),
            price: this.toNumber(extra?.price),
          }))
        : [],
    };
  }

  private loadCartFromStorage(): CartItem[] {
    const raw = localStorage.getItem(CART_STORAGE_KEY);

    try {
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed)
        ? parsed.map((item) => this.normalizeCartItem(item))
        : [];
    } catch (err) {
      console.error('Could not parse cart from localStorage', err);
      return [];
    }
  }

  addToCart(item: CartItem): void {
    const normalizedItem = this.normalizeCartItem(item);
    const existingItem = this.cart.find(
      (cartItem) => cartItem.id === normalizedItem.id,
    );

    if (existingItem) {
      existingItem.quantity += normalizedItem.quantity;
    } else {
      this.cart.push({ ...normalizedItem });
    }

    this.cartSubject.next([...this.cart]);
  }

  updateQuantity(itemId: string, quantity: number): void {
    const item = this.cart.find((cartItem) => cartItem.id === itemId);
    if (item) {
      item.quantity = quantity;
      if (item.quantity <= 0) {
        this.cart = this.cart.filter((cartItem) => cartItem.id !== itemId);
      }
    }

    this.cartSubject.next([...this.cart]);
  }

  removeItem(itemId: string): void {
    this.cart = this.cart.filter((cartItem) => cartItem.id !== itemId);
    this.cartSubject.next([...this.cart]);
  }

  getTotalPrice(): number {
    return this.cart.reduce((total, item) => {
      // const extrasTotal =
      //   item.extras?.reduce((sum, extra) => sum + extra.price, 0) ?? 0;

      return total + item.price * item.quantity;
    }, 0);
  }

  getSubtotal(items: CartItem[] = this.cart): number {
    return items.reduce((total, item) => {
      return total + item.price * item.quantity;
    }, 0);
  }

  getDeliveryFee(
    orderType: OrderType,
    deliveryFeeOriginalGross: number,
  ): number {
    return orderType === 'delivery'
      ? this.toNumber(deliveryFeeOriginalGross)
      : 0;
  }

  getSmallOrderFee(
    subtotal: number,
    minimumOrderAmount: number,
    smallOrderFee: number,
  ): number {
    return subtotal < this.toNumber(minimumOrderAmount)
      ? this.toNumber(smallOrderFee)
      : 0;
  }

  getMinimumGuardTotal(
    subtotal: number,
    minimumOrderAmount: number,
    smallOrderFee: number,
  ): number {
    return (
      subtotal +
      this.getSmallOrderFee(subtotal, minimumOrderAmount, smallOrderFee)
    );
  }

  getIsBelowMinimum(
    items: CartItem[],
    orderType: OrderType,
    subtotal: number,
    minimumOrderAmount: number,
    minimumDeliveryAmount: number,
    smallOrderFee: number,
  ): boolean {
    if (!items.length) return false;
    if (orderType !== 'delivery') return false;

    const minimumGuardTotal = this.getMinimumGuardTotal(
      subtotal,
      minimumOrderAmount,
      smallOrderFee,
    );

    return minimumGuardTotal < this.toNumber(minimumDeliveryAmount);
  }

  getAmountRemainingToMinimum(
    orderType: OrderType,
    subtotal: number,
    minimumOrderAmount: number,
    minimumDeliveryAmount: number,
    smallOrderFee: number,
  ): number {
    if (orderType !== 'delivery') return 0;

    const minimumGuardTotal = this.getMinimumGuardTotal(
      subtotal,
      minimumOrderAmount,
      smallOrderFee,
    );

    return Math.max(
      0,
      this.toNumber(minimumDeliveryAmount) - minimumGuardTotal,
    );
  }

  getTotal(config: CartSummaryConfig): number {
    const subtotal = this.getSubtotal(config.items);
    const deliveryFee = this.getDeliveryFee(
      config.orderType,
      config.deliveryFee,
    );
    const smallOrderFee = this.getSmallOrderFee(
      subtotal,
      config.minimumOrderAmount,
      config.smallOrderFee,
    );

    return (
      subtotal + deliveryFee + this.toNumber(config.serviceFee) + smallOrderFee
    );
  }

  getSummary(config: CartSummaryConfig): CartSummary {
    const subtotal = this.getSubtotal(config.items);
    const deliveryFee = this.getDeliveryFee(
      config.orderType,
      config.deliveryFee,
    );
    const serviceFee = this.toNumber(config.serviceFee);
    const smallOrderFee = this.getSmallOrderFee(
      subtotal,
      config.minimumOrderAmount,
      config.smallOrderFee,
    );
    const minimumGuardTotal = subtotal + smallOrderFee;
    const isBelowMinimum =
      config.orderType === 'delivery' &&
      config.items.length > 0 &&
      minimumGuardTotal < this.toNumber(config.minimumDeliveryAmount);

    return {
      subtotal,
      deliveryFee,
      serviceFee,
      smallOrderFee,
      total: subtotal + deliveryFee + serviceFee + smallOrderFee,
      minimumGuardTotal,
      isBelowMinimum,
      amountRemainingToMinimum:
        config.orderType === 'delivery'
          ? Math.max(
              0,
              this.toNumber(config.minimumDeliveryAmount) - minimumGuardTotal,
            )
          : 0,
    };
  }

  savePaymentSummary(snapshot: PaymentSummarySnapshot): void {
    try {
      sessionStorage.setItem(PAYMENT_SUMMARY_KEY, JSON.stringify(snapshot));
    } catch {}
  }

  getPaymentSummary(): PaymentSummarySnapshot | null {
    try {
      const raw = sessionStorage.getItem(PAYMENT_SUMMARY_KEY);
      if (!raw) return null;

      const parsed = JSON.parse(raw);

      return {
        items: Array.isArray(parsed?.items)
          ? parsed.items.map((item: any) => this.normalizeCartItem(item))
          : [],
        orderNotes: String(parsed?.orderNotes ?? ''),
        orderType: parsed?.orderType === 'delivery' ? 'delivery' : 'collection',
        deliveryFee: this.toNumber(parsed?.deliveryFee),
        serviceFee: this.toNumber(parsed?.serviceFee),
        smallOrderFee: this.toNumber(parsed?.smallOrderFee),
        total: this.toNumber(parsed?.total),
      };
    } catch {
      return null;
    }
  }

  clearPaymentSummary(): void {
    try {
      sessionStorage.removeItem(PAYMENT_SUMMARY_KEY);
    } catch {}
  }

  getOrderNotes(): string {
    try {
      return sessionStorage.getItem(ORDER_NOTES_KEY) || '';
    } catch {
      return '';
    }
  }

  saveOrderNotes(notes: string): string {
    const trimmed = String(notes || '')
      .trim()
      .slice(0, 500);

    try {
      if (trimmed) {
        sessionStorage.setItem(ORDER_NOTES_KEY, trimmed);
      } else {
        sessionStorage.removeItem(ORDER_NOTES_KEY);
      }
    } catch {}

    return trimmed;
  }

  clearCart(): void {
    this.cart = [];
    localStorage.removeItem(CART_STORAGE_KEY);
    this.cartSubject.next([]);
  }
}
